import { Head, Link } from "@inertiajs/react"
import { VerifiedIcon } from "../../components/Icons"
import { SocialLinks } from "../User/components/SocialLinks"
import { Pagination } from "./components/Pagination"
import type { DirectoryIndexProps, DirectoryCoffeeShop } from "./types"

import { Card } from "@/components/ui/card"
function ImagePlaceholder() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  )
}

function LocationLinks({ shop }: { shop: DirectoryCoffeeShop }) {
  if (!shop.district_url || !shop.state_url) return null

  return (
    <>
      <a
        href={shop.district_url}
        className="text-brand-strong hover:text-foreground-strong"
      >
        {shop.district}
      </a>
      {", "}
      <a
        href={shop.state_url}
        className="text-brand-strong hover:text-foreground-strong"
      >
        {shop.state}
      </a>
    </>
  )
}

function MobileCard({ shop }: { shop: DirectoryCoffeeShop }) {
  return (
    <li className="px-4 py-4 sm:px-6 bg-card">
      <div className="flex w-full items-center justify-between space-x-6">
        <div className="flex-1 truncate">
          <div className="flex items-center space-x-3">
            <h3 className="truncate text-sm font-medium text-foreground-primary flex items-center">
              <Link href={shop.url}>{shop.name}</Link>
              {shop.has_owner && (
                <span className="ml-1">
                  <VerifiedIcon className="w-4" />
                </span>
              )}
            </h3>
          </div>
          <p className="mt-1 truncate text-sm text-foreground-muted">
            <LocationLinks shop={shop} />
          </p>
        </div>
        <Link href={shop.url}>
          {shop.logo_url ? (
            <img
              src={shop.logo_url}
              className="h-10 w-10 flex-shrink-0 rounded-full bg-border-strong border border-brand"
              loading="lazy"
              alt={shop.name}
            />
          ) : (
            <div className="h-10 w-10 rounded-full">
              <ImagePlaceholder />
            </div>
          )}
        </Link>
      </div>
      <div className="text-sm text-foreground-muted mt-8">
        <div className="flex space-x-3.5 items-center">
          <SocialLinks links={shop.links} />
        </div>
      </div>
    </li>
  )
}

function DesktopRow({ shop }: { shop: DirectoryCoffeeShop }) {
  return (
    <tr className="bg-card">
      <td className="px-6 py-4 sticky sm:static left-0 z-0 min-w-[220px]">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10">
            <Link href={shop.url}>
              {shop.logo_url ? (
                <img
                  src={shop.logo_url}
                  className="h-10 w-10 rounded-full border border-brand"
                  loading="lazy"
                  alt={shop.name}
                />
              ) : (
                <div className="h-10 w-10 rounded-full">
                  <ImagePlaceholder />
                </div>
              )}
            </Link>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-foreground-primary flex items-center">
              <Link href={shop.url}>
                {shop.name}
                {shop.has_pru15_tag && (
                  <span className="ml-1 text-lg">
                    {"\uD83C\uDDF2\uD83C\uDDFE"}
                  </span>
                )}
              </Link>
              {shop.has_owner && (
                <span className="ml-1">
                  <VerifiedIcon className="w-4" />
                </span>
              )}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-foreground-primary">
          <LocationLinks shop={shop} />
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground-muted">
        <div className="flex space-x-2 items-center">
          <SocialLinks links={shop.links} />
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <Link
          href={shop.url}
          className="text-brand-strong hover:text-foreground-strong"
        >
          View
        </Link>
      </td>
    </tr>
  )
}

function LiveMapBar({ state }: { state: string }) {
  const mapUrl = `/?tab=map&state=${encodeURIComponent(state)}`

  return (
    <div className="mt-4 flex justify-start md:hidden">
      <span className="relative inline-flex">
        <a
          href={mapUrl}
          className="inline-flex items-center px-3 py-2 border border-border-strong shadow-sm text-sm leading-4 font-medium rounded-md text-foreground-secondary bg-card hover:bg-surface-subtle focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
        >
          <svg
            className="w-6 mr-2"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
            />
          </svg>
          Live Map
        </a>
        <span className="flex absolute h-3 w-3 top-0 right-0 -mt-1 -mr-1">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-sky-500"></span>
        </span>
      </span>
    </div>
  )
}

function NoResults({ newSubmissionPath }: { newSubmissionPath: string }) {
  return (
    <Card className="mt-4 overflow-hidden rounded-lg border-0">
      <div className="px-4 py-5 sm:p-6">
        <div className="text-center">
          <svg
            className="mx-auto h-12 w-12 text-foreground-faint"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-foreground-primary">
            No coffee shop
          </h3>
          <p className="mt-1 text-sm text-foreground-muted">
            Be the first one to submit the coffee shop here.
          </p>
          <div className="mt-6">
            <a
              href={newSubmissionPath}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-brand-foreground bg-brand-strong hover:bg-brand-ink focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
            >
              <svg
                className="-ml-1 mr-2 h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
              New Coffee Shop
            </a>
          </div>
        </div>
      </div>
    </Card>
  )
}

function buildPageUrl(
  state: string,
  district: string | null,
  page: number
): string {
  const base = district
    ? `/directories/${state.toLowerCase().replace(/\s+/g, "-")}/${district.toLowerCase().replace(/\s+/g, "-")}`
    : `/directories/${state.toLowerCase().replace(/\s+/g, "-")}`

  return page === 1 ? base : `${base}?page=${page}`
}

export default function DirectoryIndex({
  coffee_shops,
  pagination,
  state,
  district,
  page_info,
  new_submission_path,
}: DirectoryIndexProps) {
  const pageUrl = (page: number) => buildPageUrl(state, district, page)

  return (
    <>
      <Head title={page_info} />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="min-h-full flex flex-col justify-center mb-12 mt-4 lg:mt-6">
          <h1 className="text-lg font-medium text-foreground-primary">
            <a href="/" className="mr-2">
              &#8592;
            </a>
            {page_info}
          </h1>

          {coffee_shops.length > 0 ? (
            <>
              <LiveMapBar state={state} />

              {/* Mobile */}
              <Card className="mt-4 overflow-hidden rounded-none border-0 sm:rounded-md sm:hidden">
                <ul role="list" className="divide-y divide-border-default">
                  {coffee_shops.map((shop) => (
                    <MobileCard key={shop.slug} shop={shop} />
                  ))}
                </ul>
              </Card>

              {/* Desktop */}
              <div className="hidden sm:block">
                <div className="mt-4 flex flex-col">
                  <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                      <div className="shadow border-b border-border-default sm:rounded-lg">
                        <table className="min-w-full divide-y divide-border-default">
                          <tbody className="bg-card divide-y divide-border-default">
                            {coffee_shops.map((shop) => (
                              <DesktopRow key={shop.slug} shop={shop} />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto whitespace-nowrap">
                <div className="py-3 flex justify-end">
                  <Pagination pagination={pagination} buildUrl={pageUrl} />
                </div>
              </div>
            </>
          ) : (
            <NoResults newSubmissionPath={new_submission_path} />
          )}
        </div>
      </div>
    </>
  )
}
